import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useApply } from "@/context/ApplyContext";
import { CTA_TEXT } from "@/data/content";

export const Section = ({ id, className, children }) => (
  <section id={id} className={cn("px-6 py-16 md:py-24", className)}>
    {children}
  </section>
);

export const FadeUp = ({ children, delay = 0, className }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.5, delay, ease: "easeOut" }}
    className={className}
  >
    {children}
  </motion.div>
);

export const Eyebrow = ({ children, dark }) => (
  <p className={cn("text-sm uppercase tracking-[0.2em] font-bold mb-4", dark ? "text-[#F87171]" : "text-[#D90429]")}>
    {children}
  </p>
);

export const SectionHeading = ({ children, dark, className }) => (
  <h2 className={cn("text-3xl md:text-4xl font-bold tracking-tight leading-tight", dark ? "text-white" : "text-zinc-900", className)}>
    {children}
  </h2>
);

export const Lead = ({ children, className }) => (
  <p className={cn("text-lg md:text-xl text-zinc-700 leading-relaxed", className)}>{children}</p>
);

export const CtaButton = ({ testid, microcopy, dark, className }) => {
  const { openApply } = useApply();
  return (
    <div className={cn("flex flex-col items-center gap-3", className)}>
      <button
        data-testid={testid}
        onClick={openApply}
        className="w-full md:w-auto bg-[#D90429] hover:bg-[#B90323] text-white px-8 py-5 rounded-lg font-bold text-lg md:text-xl uppercase tracking-wide shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-[#D90429]/40"
      >
        {CTA_TEXT}
      </button>
      {microcopy && (
        <p className={cn("text-sm font-medium", dark ? "text-zinc-400" : "text-zinc-500")}>{microcopy}</p>
      )}
    </div>
  );
};

export const CheckItem = ({ children, variant = "check" }) => (
  <li className="flex items-start gap-3">
    <span
      className={cn(
        "mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
        variant === "check" ? "bg-zinc-900 text-white" : "bg-zinc-200 text-zinc-500"
      )}
    >
      {variant === "check" ? <Check className="h-4 w-4" strokeWidth={3} /> : <span className="text-lg font-bold leading-none">&times;</span>}
    </span>
    <span className="text-lg md:text-xl text-zinc-700 leading-relaxed">{children}</span>
  </li>
);
